// client/src/components/pages/LexiStar/Billing/StatementPrint.tsx
//
// /lexistar/billing/statement/:month — the usage statement as a document on its own (ST-00207
// stream A; m4b-contracts §2.5): the page report definition `usage_statement` points at, so the
// report engine prints this page for the PDF (?print=1&run=<run_id>, signed in with the run's
// one-time report session). With `run` the page reads the run's pinned data snapshot
// (GET /reports/runs/:id/data: the statement and its trucks as the provider saw them), so the
// PDF is reproducible; without it, the organisation's stored statement for the month
// (?tenant_id= for a global admin). Same StatementDocument as the Usage & billing page: a flat
// monthly statement (ST-00241) prints each truck's activation, charge and notice from its line.
// Signals data-report-ready / data-report-error for the renderer through ReportFrame.

import { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaDesktop, FaPrint } from 'react-icons/fa';
import { getRunData, isObj } from '../../../../services/lexistarReports';
import { billingErrorText, getAssetDays, getStatement, listStatements } from '../../../../services/lexistarBilling';
import { PaperMessage, ReportFrame, ReportRenderContext, toolbarButton, usePrintMode, type LoadState } from '../Reports/reportAtoms';
import { useAuth } from '../../../../types/commonComponents';
import type { AssetDayRow, UsageStatement } from '../../../../types/lexistar-billing';
import StatementDocument from './StatementDocument';
import { flatLineOf, isMonth, monthLabel, monthOf } from './billingLogic';

interface Loaded {
  statement: UsageStatement;
  assets: AssetDayRow[];
}

/** The statement and trucks out of a usage_statement run's data snapshot (the provider's sections). */
export function fromRunData(data: unknown): Loaded | null {
  if (!isObj(data) || !isObj(data.sections)) return null;
  const s = data.sections.statement;
  if (!isObj(s) || !Array.isArray(s.lines)) return null;
  const assets = Array.isArray(data.sections.asset_days) ? (data.sections.asset_days as AssetDayRow[]) : [];
  return { statement: s as unknown as UsageStatement, assets };
}

export default function StatementPrint() {
  const { month = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const print = usePrintMode();
  const rendering = useContext(ReportRenderContext);
  const { hasPermission } = useAuth();
  const canView = hasPermission('lexistar.billing.view');
  const runId = params.get('run');
  const tenantId = Number(params.get('tenant_id')) || null;

  const [state, setState] = useState<{ load: LoadState; data: Loaded | null; error: string | null }>({ load: 'loading', data: null, error: null });

  const load = useCallback(async () => {
    if (!canView) return;
    if (!isMonth(month)) {
      setState({ load: 'error', data: null, error: 'Not a month (YYYY-MM).' });
      return;
    }
    try {
      if (runId) {
        const snap = await getRunData(runId);
        const data = fromRunData(snap.data);
        if (!data) throw new Error('This report run holds no usage statement.');
        setState({ load: 'ready', data, error: null });
        return;
      }
      const list = await listStatements({ from: month, to: month, tenantId });
      const found = list.find((s) => monthOf(s.period_month) === month);
      if (!found) {
        setState({ load: 'error', data: null, error: `No statement has been built for ${monthLabel(month)} yet.` });
        return;
      }
      const statement = await getStatement(found.id);
      // A flat monthly statement (ST-00241) carries its trucks in its line; a truck-days one needs its asset-days.
      const assets = flatLineOf(statement.lines) ? [] : (await getAssetDays(month, tenantId)).assets;
      setState({ load: 'ready', data: { statement, assets }, error: null });
    } catch (err) {
      setState({ load: 'error', data: null, error: billingErrorText(err, (err as Error)?.message || 'The statement could not be loaded.') });
    }
  }, [canView, month, runId, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!canView) {
    return (
      <ReportFrame print={print} state="error" error="forbidden">
        <PaperMessage print={print} title="You do not have permission to view usage statements." />
      </ReportFrame>
    );
  }

  const setPrint = (on: boolean) => {
    const next = new URLSearchParams(params);
    if (on) next.set('print', '1');
    else next.delete('print');
    setParams(next, { replace: true });
  };

  const back = `/lexistar/billing?month=${encodeURIComponent(month)}${tenantId ? `&tenant=${tenantId}` : ''}`;
  const toolbar = rendering ? null : (
    <>
      <Link to={back} className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900">
        <FaArrowLeft size={10} aria-hidden="true" /> Usage & billing
      </Link>
      <div className="flex flex-wrap items-center gap-2">
        {print ? (
          <>
            <button type="button" className={toolbarButton} onClick={() => setPrint(false)}>
              <FaDesktop size={11} aria-hidden="true" /> Screen view
            </button>
            <button type="button" className={toolbarButton} onClick={() => window.print()}>
              <FaPrint size={11} aria-hidden="true" /> Print
            </button>
          </>
        ) : (
          <button type="button" className={toolbarButton} onClick={() => setPrint(true)}>
            <FaPrint size={11} aria-hidden="true" /> Print layout
          </button>
        )}
      </div>
    </>
  );

  return (
    <ReportFrame print={print} state={state.load} error={state.error} toolbar={toolbar}>
      {state.load === 'loading' ? (
        <PaperMessage print={print} title="Loading the usage statement…" />
      ) : state.error || !state.data ? (
        <PaperMessage print={print} title={state.error || 'The statement could not be loaded.'} />
      ) : (
        <StatementDocument statement={state.data.statement} assets={state.data.assets} showAssets print={print} />
      )}
    </ReportFrame>
  );
}
